import { useEffect } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useDispatch } from "react-redux";
import { Outlet, useNavigate } from "react-router-dom";

import DisableSwipeBack from "./DisableSwipe";

import ErrorPage from "@/components/state-pages/ErrorPage";
import { SOCKET_KEYS } from "@/constants/socket.constant";
import { useApiInvalidator } from "@/hooks/useApiInvalidator";
import { useSocket } from "@/hooks/useSocket";
import { ICustomer } from "@/interfaces/customer.interface";
import MainLayout from "@/layouts/MainLayout";
import {
  customerGeneralApi,
  useGetUserInfoQuery,
  useLazyGetCustomerInfoQuery,
} from "@/redux/api/customer-portal/GeneralQuery";
import { refetchCustomerData, refetchUserData } from "@/redux/slices/userSlice";
import { getToken } from "@/utils/helpers";

type TProps = {
  redirectPath?: string;
};

const C_ProtectedRoutes: React.FC<TProps> = ({ redirectPath = "/login" }) => {
  const data: ICustomer = JSON.parse(
    localStorage.getItem("customer-data") ?? "{}",
  );
  const { data: userData, isLoading } = useGetUserInfoQuery(data?.id, {
    skip: !getToken() || !data.id,
  });

  const navigate = useNavigate();

  const dispatch = useDispatch();

  useEffect(() => {
    if (!data || !getToken()) {
      localStorage.clear();
      navigate(redirectPath);
    }
    if (userData) {
      localStorage.setItem("userData", JSON.stringify(userData));
    }
    dispatch(refetchUserData());
  }, [data, window.location.href, userData, isLoading]);

  const { socket } = useSocket("/");
  const { invalidateTags } = useApiInvalidator<typeof customerGeneralApi>();

  const [getCustomerInfo] = useLazyGetCustomerInfoQuery();

  const handleCustomerData = async (): Promise<void> => {
    const customerData = await getCustomerInfo(data?.id);

    if (customerData && customerData.data) {
      localStorage.setItem("customer-data", JSON.stringify(customerData.data));
    }
  };

  useEffect(() => {
    socket?.on(SOCKET_KEYS.CLIENTS_CUSTOMER, async () => {
      await handleCustomerData();
      dispatch(refetchCustomerData());
    });
  }, [socket, invalidateTags]);

  if (!data) {
    return <ErrorPage />;
  }

  return (
    <>
      <MainLayout>
        <HelmetProvider>
          <Helmet>
            <title>{data?.officialName}</title>
            <link rel='icon' href={data?.logo ?? "/fav.png"} sizes='16x16' />
          </Helmet>
        </HelmetProvider>
        <DisableSwipeBack />
        <Outlet />
      </MainLayout>
    </>
  );
};

export default C_ProtectedRoutes;
